import {
  Alert, Box, Chip, Divider, Stack, Tooltip, Typography,
} from '@mui/material';
import { memo, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslate } from 'react-admin';
import { Asset, SecurityInventoryCategory, CertificationCategory } from '@x-guard/xgac-types/xgac';
import { calculateDaysUntilDate, daysUntilNextPostExpiryWeeklyReminder } from '../../../utils/certificationsAndItemsExpiry';
import {
  TimeGranularityLabel,
  convertTimeGranularityToDays,
  roundDaysToExpiryTimeGranularity,
} from '../../../utils/timeGranularityUtils';

type RecipientsChipProps = {
  numberOfRecipients: number;
  responsibleUsers: { id: string; label: string }[];
  certifiedUser: string | null;
};

const RecipientsChip = memo((props: RecipientsChipProps) => {

  const translate = useTranslate();

  const recipientsLabel = props.numberOfRecipients === 1
    ? props.certifiedUser || props.responsibleUsers[0].label
    : translate('general.text.users', { count: props.numberOfRecipients });

  if (props.numberOfRecipients === 1) {

    return <Chip label={recipientsLabel} size="small" />;

  }

  return (
    <Tooltip
      title={
        <Stack spacing={1}>
          {props.responsibleUsers.length > 0 && (
            <Box>
              <Typography>{translate('general.text.responsible_users', { smart_count: props.responsibleUsers.length })}</Typography>
              {props.responsibleUsers.map((recipient) => (
                <Typography key={recipient.id}>{recipient.label}</Typography>
              ))}
            </Box>
          )}
          {props.responsibleUsers.length > 0 && props.certifiedUser && (
            <Divider sx={{ height: '2px', backgroundColor: 'white' }} />
          )}
          {props.certifiedUser && (
            <Box>
              <Typography>
                {translate('general.text.certified_users')}
              </Typography>
              <Typography>{props.certifiedUser}</Typography>
            </Box>
          )}
        </Stack>
      }
    >
      <Chip label={recipientsLabel} size="small" />
    </Tooltip>
  );

});

type NextEmailAlertProps = {
  type: 'security-inventory';
  category: SecurityInventoryCategory | null;
  users: Asset[];
  certifiedUserName?: never;
} | {
  type: 'certification';
  category: CertificationCategory | null;
  users: Asset[];
  certifiedUserName: string | null;
};

export const NextEmailAlert = (props: NextEmailAlertProps) => {

  const translate = useTranslate();
  const expiryDate = useWatch({ name: 'expiryDate' }) as string | undefined;
  const plannedRenewalDate = useWatch({ name: 'plannedRenewalDate' }) as string | undefined;

  const { responsibleUsers, certifiedUser } = useMemo(() => {

    const peopleToNotifyIds = new Set(
      (props.category?.notifiedPeople.peopleToNotify ?? [])
        .map((person) => (typeof person === 'string' ? person : person._id)),
    );

    const users = props.users.filter((user) => peopleToNotifyIds.has(user._id)).map((user) => ({ id: user._id, label: user.name }));
    const emails = (props.category?.notifiedPeople.emails ?? []).map((email) => ({ id: `email:${email}`, label: email }));

    if (props.type === 'certification' && props.category?.notifiedPeople.isCertificationHolderNotified && props.certifiedUserName) {

      return { responsibleUsers: [...users, ...emails], certifiedUser: props.certifiedUserName };

    }

    return { responsibleUsers: [...users, ...emails] };

  }, [props.category, props.users, props.type, props.certifiedUserName]);

  const { daysUntilNextEmail, isExpired } = useMemo(() => {

    const daysUntilPlannedRenewalDate = plannedRenewalDate
      ? calculateDaysUntilDate(plannedRenewalDate)
      : null;

    if (daysUntilPlannedRenewalDate !== null && daysUntilPlannedRenewalDate >= 0) {

      return {
        daysUntilNextEmail: daysUntilPlannedRenewalDate,
        isExpired: false,
      };

    }

    if (!expiryDate || !props.category) {

      return { daysUntilNextEmail: null, isExpired: false };

    }
    const daysUntilExpiry = calculateDaysUntilDate(expiryDate);

    if (daysUntilExpiry < 0) {

      return {
        daysUntilNextEmail: daysUntilNextPostExpiryWeeklyReminder(daysUntilExpiry),
        isExpired: true,
      };

    }

    const reminderPeriods = [
      ...props.category.reminderPeriods.map((reminderPeriod) => (
        daysUntilExpiry - convertTimeGranularityToDays(reminderPeriod)
      )),
      daysUntilExpiry,
    ].filter((daysUntilEmail) => daysUntilEmail >= 0);

    return {
      daysUntilNextEmail: reminderPeriods.length > 0
        ? Math.min(...reminderPeriods)
        : daysUntilExpiry,
      isExpired: false,
    };

  }, [plannedRenewalDate, expiryDate, props.category]);

  if (!props.category) {

    return null;

  }
  if (daysUntilNextEmail === null) {

    return (
      <Alert severity="warning" color="warning" variant="outlined" sx={{ alignItems: 'center' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5,
        }}>
          <Typography>{translate('general.text.invalid_expiry_date')}</Typography>
        </Box>
      </Alert>
    );

  }

  const numberOfRecipients = certifiedUser ? responsibleUsers.length + 1 : responsibleUsers.length;
  const hasRecipients = numberOfRecipients > 0;

  return (
    <Alert
      severity={!hasRecipients || isExpired ? 'warning' : 'info'}
      color={!hasRecipients || isExpired ? 'warning' : 'success'}
      variant="outlined"
      sx={{ alignItems: 'center' }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5,
      }}>
        {isExpired && (
          <Typography component="div" sx={{ width: '100%' }}>
            {!hasRecipients ? (
              <Typography>
                {translate('general.text.email_will_not_be_sent', { smart_count: numberOfRecipients })}
              </Typography>
            ) : (
              <>
                <RecipientsChip
                  numberOfRecipients={numberOfRecipients}
                  responsibleUsers={responsibleUsers}
                  certifiedUser={certifiedUser || null}
                />
                {' '}
                {translate('general.text.will_receive', {
                  smart_count: numberOfRecipients,
                  categoryName: props.category.name,
                })}
                {props.type === 'security-inventory' ? <br /> : ' '}
                {(() => {

                  const daysPastExpiry = (() => {

                    if (!expiryDate) return 0;
                    return Math.abs(Math.min(0, calculateDaysUntilDate(expiryDate)));

                  })();

                  const expiredStatus = (() => {

                    if (daysPastExpiry === 1) {

                      return props.type === 'security-inventory'
                        ? translate('general.text.item_expired_reminder_continues', { smart_count: 1 })
                        : translate('general.text.certification_expired_reminder_continues', { smart_count: 1 });

                    }

                    const period = TimeGranularityLabel(
                      roundDaysToExpiryTimeGranularity(daysPastExpiry),
                      translate,
                    );

                    return props.type === 'security-inventory'
                      ? translate('general.text.item_expired_reminder_continues_period', { period })
                      : translate('general.text.certification_expired_reminder_continues_period', { period });

                  })();

                  const nextSend = (() => {

                    if (daysUntilNextEmail === 0) {

                      return translate('general.text.will_be_sent_today');

                    }

                    if (daysUntilNextEmail === 1) {

                      return translate('general.text.email_will_be_sent', { smart_count: 1 });

                    }

                    return translate('general.text.email_will_be_sent', {
                      smart_count: daysUntilNextEmail,
                    });

                  })();

                  return (
                    <>
                      {expiredStatus}
                      {' '}
                      {nextSend}
                    </>
                  );

                })()}
              </>
            )}
          </Typography>
        )}
        {!isExpired && (
          !hasRecipients ? (
            <Typography>
              {translate('general.text.email_will_not_be_sent', { smart_count: numberOfRecipients })}
            </Typography>
          ) : (
            <Typography component="div" sx={{ width: '100%' }}>
              <RecipientsChip
                numberOfRecipients={numberOfRecipients}
                responsibleUsers={responsibleUsers}
                certifiedUser={certifiedUser || null}
              />
              {' '}
              {translate('general.text.will_receive', { smart_count: numberOfRecipients, categoryName: props.category.name })}
              {props.type === 'security-inventory' ? (<br />) : ' '}
              {(() => {

                if (daysUntilNextEmail === 0) {

                  return translate('general.text.will_be_sent_today');

                }

                if (daysUntilNextEmail === 1) {

                  return translate('general.text.email_will_be_sent', { smart_count: 1 });

                }

                return translate('general.text.email_will_be_sent_period', {
                  period: TimeGranularityLabel(
                    roundDaysToExpiryTimeGranularity(daysUntilNextEmail),
                    translate,
                  ),
                });

              })()}
            </Typography>
          )
        )}

      </Box>
    </Alert>
  );

};
